import moment from "moment";

// UI language → moment locale. Swiss and Valais German use German dates.
const LOCALE_MAP = {
  de: "de", gsw: "de", wae: "de", en: "en", fr: "fr", it: "it", es: "es", pt: "pt", ru: "ru", pl: "pl",
  nl: "nl", tr: "tr", el: "el", cs: "cs", hu: "hu", ro: "ro", bg: "bg", hr: "hr", sl: "sl", sk: "sk",
  sv: "sv", no: "nb", da: "da", fi: "fi", zh: "zh-cn", hi: "hi", ar: "ar", ja: "ja", ko: "ko", vi: "vi",
  id: "id", ms: "ms", bn: "bn", ta: "ta", te: "te", mr: "mr",
};

// Loading a locale makes it moment's global one; the rest of the app keeps its own.
{
  const globalLocale = moment.locale();
  require("moment/locale/de");
  require("moment/locale/fr");
  require("moment/locale/it");
  require("moment/locale/es");
  require("moment/locale/pt");
  require("moment/locale/ru");
  require("moment/locale/pl");
  require("moment/locale/nl");
  require("moment/locale/tr");
  require("moment/locale/el");
  require("moment/locale/cs");
  require("moment/locale/hu");
  require("moment/locale/ro");
  require("moment/locale/bg");
  require("moment/locale/hr");
  require("moment/locale/sl");
  require("moment/locale/sk");
  require("moment/locale/sv");
  require("moment/locale/nb");
  require("moment/locale/da");
  require("moment/locale/fi");
  require("moment/locale/zh-cn");
  require("moment/locale/hi");
  require("moment/locale/ar");
  require("moment/locale/ja");
  require("moment/locale/ko");
  require("moment/locale/vi");
  require("moment/locale/id");
  require("moment/locale/ms");
  require("moment/locale/bn");
  require("moment/locale/ta");
  require("moment/locale/te");
  require("moment/locale/mr");
  moment.locale(globalLocale);
}

export function cockpitLocale(language) {
  const code = String(language || "de").split("-")[0].toLowerCase();
  return LOCALE_MAP[code] || "en";
}

/** German and English keep their patterns; every other language gets its own order from Intl. */
function intlDate(date, language, options) {
  const code = String(language || "").split("-")[0].toLowerCase();
  if (!code || code === "de" || code === "en" || code === "gsw" || code === "wae") return null;
  try {
    return new Intl.DateTimeFormat(code, options).format(moment(date).toDate());
  } catch (_) {
    return null;
  }
}

export function formatLongDate(date, language) {
  return (
    intlDate(date, language, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) ||
    moment(date).locale(cockpitLocale(language)).format("dddd, D. MMMM YYYY")
  );
}

/** Greeting caption: "Mittwoch, 21. Oktober" (en "Wednesday, 21 October"), as the app's Pro header. */
export function formatGreetingDate(date, language) {
  const intl = intlDate(date, language, { weekday: "long", day: "numeric", month: "long" });
  if (intl) return intl;
  const locale = cockpitLocale(language);
  return moment(date).locale(locale).format(locale === "en" ? "dddd, D MMMM" : "dddd, D. MMMM");
}

/** A tile of the cockpit's day strip ("YYYY-MM-DD"): "Di" + "03" (en "Tue" + "03"). */
export function formatDayTile(dayKey, language) {
  const m = moment(dayKey, "YYYY-MM-DD").locale(cockpitLocale(language));
  return { weekday: m.format("ddd").replace(/\.$/, ""), date: m.format("DD") };
}

/** The week of a day ("YYYY-MM-DD"), Monday to Sunday, as seven day keys. */
export function weekDayKeys(dayKey) {
  const monday = moment(dayKey, "YYYY-MM-DD").isoWeekday(1);
  return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, "day").format("YYYY-MM-DD"));
}

/** The ISO calendar week of a day ("YYYY-MM-DD"): 45. */
export function calendarWeek(dayKey) {
  return moment(dayKey, "YYYY-MM-DD").isoWeek();
}

/** The same weekday `weeks` weeks later (or earlier, negative) as "YYYY-MM-DD". */
export function shiftWeeks(dayKey, weeks) {
  return moment(dayKey, "YYYY-MM-DD").add(weeks * 7, "day").format("YYYY-MM-DD");
}

export function formatShortDate(date, language) {
  return intlDate(date, language, { day: "numeric", month: "short" }) || moment(date).locale(cockpitLocale(language)).format("D. MMM");
}

export function formatTime(date) {
  return moment(date).format("HH:mm");
}

/** "08:42" today, "Gestern" yesterday, "12. Mai" otherwise. */
export function formatRelativeDay(date, strings, language) {
  if (!date) return "";
  const m = moment(date);
  if (!m.isValid()) return "";
  const now = moment();
  if (m.isSame(now, "day")) return m.format("HH:mm");
  if (m.isSame(now.clone().subtract(1, "day"), "day")) return strings.cockpit_yesterday || "Gestern";
  return formatShortDate(m, language);
}

/** "heute um 08:42", "gestern um 18:00", "28.10.2026, 18:00" — a report's "Stand". */
export function formatAsOf(date, language) {
  if (!date) return "";
  const m = moment(date);
  return m.isValid() ? m.locale(cockpitLocale(language)).calendar(null, { sameElse: "DD.MM.YYYY, HH:mm" }) : "";
}

/** "vor 12 Min." style relative time. */
export function formatFromNow(date, language) {
  if (!date) return "";
  const m = moment(date);
  return m.isValid() ? m.locale(cockpitLocale(language)).fromNow() : "";
}

export function greeting(strings, date = new Date()) {
  const h = date.getHours();
  if (h < 11) return strings.cockpit_greeting_morning || "Guten Morgen";
  if (h < 18) return strings.cockpit_greeting_day || "Guten Tag";
  return strings.cockpit_greeting_evening || "Guten Abend";
}

export function initialsOf(firstName, lastName, fallback) {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  const out = `${a}${b}`.toUpperCase();
  if (out) return out;
  const f = String(fallback || "").trim();
  return f ? f.substring(0, 2).toUpperCase() : "";
}

export function stripHtml(html, max = 200) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

export function fullName(user) {
  if (!user) return "";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.name || "";
}

/** Text with a single {{n}} placeholder. */
export function fill(template, values) {
  let out = String(template || "");
  Object.keys(values || {}).forEach((k) => {
    out = out.replace(new RegExp(`{{${k}}}`, "g"), values[k]);
  });
  return out;
}

/** "2026-10-23" → "heute" / "morgen" / "überfällig · 21.10." / "23.10.". */
export function bookmarkDeadlineLabel(iso, strings = {}) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const tomorrow = `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
  const short = `${m[3]}.${m[2]}.`;
  if (iso === today) return strings.today_label || "heute";
  if (iso === tomorrow) return strings.tomorrow_label || "morgen";
  if (iso < today) return `${strings.cockpit_todo_overdue || "überfällig"} · ${short}`;
  return short;
}
